import React, { useState, useMemo } from 'react';
import useSimulationStore from '../../store/simulationStore';
import { shallow } from 'zustand/shallow';

const SpeciesPanel: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const {
    availableSpecies,
    availableReactions,
    activeChemistryConfig,
    speciesFocus,
    speciesColors,
    elementColors,
    speciesColorMode,
    focusSpecies,
    unfocusSpecies,
    setSpeciesColor,
    setElementColor,
    setSpeciesColorMode,
    resetSpeciesToElementColors,
    clearAllFocus
  } = useSimulationStore(state => ({
    availableSpecies: state.availableSpecies,
    availableReactions: state.availableReactions,
    activeChemistryConfig: state.activeChemistryConfig,
    speciesFocus: state.speciesFocus,
    speciesColors: state.speciesColors,
    elementColors: state.elementColors,
    speciesColorMode: state.speciesColorMode,
    focusSpecies: state.focusSpecies,
    unfocusSpecies: state.unfocusSpecies,
    setSpeciesColor: state.setSpeciesColor,
    setElementColor: state.setElementColor,
    setSpeciesColorMode: state.setSpeciesColorMode,
    resetSpeciesToElementColors: state.resetSpeciesToElementColors,
    clearAllFocus: state.clearAllFocus
  }), shallow);

  // Get only species that are currently involved in ACTIVE reactions
  // This is for the species display/control panel, not for reaction editing
  const activeSpecies = useMemo(() => {
    const activeSpeciesIds = new Set<string>();

    // Collect species IDs from ACTIVE reactions only
    activeChemistryConfig.reactions.forEach(reaction => {
      Object.keys(reaction.reactantsCoeff).forEach(speciesId => activeSpeciesIds.add(speciesId));
      Object.keys(reaction.productsCoeff).forEach(speciesId => activeSpeciesIds.add(speciesId));
    });

    // Filter available species to only those currently active
    const activeSpecies = availableSpecies.filter(species => activeSpeciesIds.has(species.id));

    // console.log('[SpeciesPanel] Found active species:', activeSpecies.map(s => s.name));
    return activeSpecies;
  }, [availableSpecies, activeChemistryConfig.reactions]);

  // Get species with their focus and color states
  const speciesWithStates = useMemo(() => {
    return activeSpecies.map(species => {
      const colorMode = speciesColorMode[species.id] || 'element';
      const isSpeciesMode = colorMode === 'species';
      const currentColor = isSpeciesMode ? speciesColors[species.id] : null;

      return {
        ...species,
        isFocused: speciesFocus[species.id]?.focusLevel === 'focus',
        focusSource: speciesFocus[species.id]?.focusSource || null,
        colorMode,
        isSpeciesMode,
        currentColor,
        // For display purposes, show the effective color
        displayColor: currentColor || '#FFFFFF'
      };
    });
  }, [activeSpecies, speciesFocus, speciesColors, speciesColorMode]);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSpeciesFocus = (speciesId: string, isCurrentlyFocused: boolean) => {
    if (isCurrentlyFocused) {
      unfocusSpecies(speciesId);
    } else {
      focusSpecies(speciesId, 'direct');
    }
  };

  const handleSpeciesColorChange = (speciesId: string, color: string) => {
    setSpeciesColor(speciesId, color);
  };

  const handleElementColorChange = (element: string, color: string) => {
    setElementColor(element, color);
  };

  const handleColorModeToggle = (speciesId: string, currentMode: string) => {
    const newMode = currentMode === 'element' ? 'species' : 'element';
    setSpeciesColorMode(speciesId, newMode);
  };

  const handleResetToElementColors = (speciesId: string) => {
    resetSpeciesToElementColors(speciesId);
  };

  const handleClearAllFocus = () => {
    clearAllFocus();
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle} onClick={toggleExpansion}>
        <span>Species</span>
        <div style={headerActionsStyle}>
          <button
            style={clearButtonStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleClearAllFocus();
            }}
            title="Clear all focus"
          >
            Clear Focus
          </button>
          <button style={buttonStyle}>{isExpanded ? '-' : '+'}</button>
        </div>
      </div>

      {isExpanded && (
        <div style={contentStyle}>
          {speciesWithStates.length === 0 ? (
            <div style={placeholderStyle}>No species available</div>
          ) : (
            <div style={speciesListStyle}>
              {speciesWithStates.map((species) => (
                <div key={species.id} style={speciesItemStyle}>
                  <div style={speciesHeaderStyle}>
                    <label style={focusToggleStyle}>
                      <input
                        type="checkbox"
                        checked={species.isFocused}
                        onChange={() => handleSpeciesFocus(species.id, species.isFocused)}
                        style={checkboxStyle}
                      />
                      <span style={speciesNameStyle}>{species.name}</span>
                    </label>
                    {species.focusSource && (
                      <span style={focusSourceStyle}>
                        {species.focusSource === 'direct' ? '👆' : '⚗️'}
                      </span>
                    )}
                  </div>

                  <div style={colorControlsStyle}>
                    <div style={colorModeHeaderStyle}>
                      <span style={colorLabelStyle}>Color Mode:</span>
                      <button
                        style={{
                          ...colorModeButtonStyle,
                          ...(species.isSpeciesMode ? activeColorModeStyle : {})
                        }}
                        onClick={() => handleColorModeToggle(species.id, species.colorMode)}
                        title={species.isSpeciesMode ? 'Switch to element colors' : 'Switch to species color'}
                      >
                        {species.isSpeciesMode ? '🔬 Species' : '⚛️ Element'}
                      </button>
                      {species.isSpeciesMode && (
                        <button
                          style={resetButtonStyle}
                          onClick={() => handleResetToElementColors(species.id)}
                          title="Reset to element colors"
                        >
                          🔄
                        </button>
                      )}
                    </div>

                    {species.isSpeciesMode ? (
                      <div style={colorInputContainerStyle}>
                        <label style={colorLabelStyle}>Species Color:</label>
                        <input
                          type="color"
                          value={species.currentColor || '#FFFFFF'}
                          onChange={(e) => handleSpeciesColorChange(species.id, e.target.value)}
                          style={colorInputStyle}
                        />
                        <input
                          type="text"
                          value={species.currentColor || '#FFFFFF'}
                          onChange={(e) => handleSpeciesColorChange(species.id, e.target.value)}
                          style={colorTextInputStyle}
                          placeholder="#FFFFFF"
                        />
                      </div>
                    ) : (
                      <div style={elementColorsStyle}>
                        <span style={colorLabelStyle}>Element Colors:</span>
                        <div style={elementColorListStyle}>
                          {/* This would need to be populated based on the species composition */}
                          <div style={elementColorItemStyle}>
                            <span style={elementLabelStyle}>H:</span>
                            <input
                              type="color"
                              value={elementColors['H'] || '#FFFFFF'}
                              onChange={(e) => handleElementColorChange('H', e.target.value)}
                              style={elementColorInputStyle}
                            />
                          </div>
                          <div style={elementColorItemStyle}>
                            <span style={elementLabelStyle}>O:</span>
                            <input
                              type="color"
                              value={elementColors['O'] || '#FF0000'}
                              onChange={(e) => handleElementColorChange('O', e.target.value)}
                              style={elementColorInputStyle}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {species.isFocused && (
                    <div style={focusedIndicatorStyle}>
                      <span style={focusedTextStyle}>FOCUSED</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '10px',
  width: 'auto',
  minWidth: '280px',
  maxWidth: '40vw',
  maxHeight: '400px',
  backgroundColor: 'rgba(40, 40, 40, 0.9)',
  color: '#eee',
  border: '1px solid #555',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'monospace',
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: 'rgba(60, 60, 60, 0.9)',
  fontWeight: 'bold',
  flexShrink: 0,
  borderBottom: '1px solid #555',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const headerActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const buttonStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #888',
  color: '#eee',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  lineHeight: '1',
};

const clearButtonStyle: React.CSSProperties = {
  background: 'rgba(200, 60, 60, 0.8)',
  border: '1px solid #aa4444',
  color: '#fff',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  lineHeight: '1',
  borderRadius: '2px',
};

const contentStyle: React.CSSProperties = {
  overflowY: 'auto',
  padding: '8px',
  flexGrow: 1,
  minHeight: 0,
};

const placeholderStyle: React.CSSProperties = {
  fontStyle: 'italic',
  color: '#aaa',
  textAlign: 'center',
  padding: '20px',
};

const speciesListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const speciesItemStyle: React.CSSProperties = {
  backgroundColor: 'rgba(30, 30, 30, 0.8)',
  border: '1px solid #444',
  borderRadius: '4px',
  padding: '8px',
  position: 'relative',
};

const speciesHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '6px',
};

const focusToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  userSelect: 'none',
};

const checkboxStyle: React.CSSProperties = {
  margin: 0,
  transform: 'scale(1.1)',
};

const speciesNameStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: '13px',
};

const focusSourceStyle: React.CSSProperties = {
  fontSize: '12px',
  opacity: 0.7,
};

const colorPickerContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px',
};

const colorLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#ccc',
  minWidth: '40px',
};

const colorInputContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const colorInputStyle: React.CSSProperties = {
  width: '24px',
  height: '20px',
  border: '1px solid #666',
  borderRadius: '2px',
  cursor: 'pointer',
};

const colorTextInputStyle: React.CSSProperties = {
  width: '70px',
  height: '20px',
  backgroundColor: 'rgba(20, 20, 20, 0.8)',
  border: '1px solid #666',
  borderRadius: '2px',
  color: '#eee',
  fontSize: '10px',
  padding: '2px 4px',
  fontFamily: 'monospace',
};

const focusedIndicatorStyle: React.CSSProperties = {
  position: 'absolute',
  top: '4px',
  right: '4px',
  backgroundColor: 'rgba(0, 150, 255, 0.8)',
  borderRadius: '2px',
  padding: '1px 4px',
};

const focusedTextStyle: React.CSSProperties = {
  fontSize: '8px',
  fontWeight: 'bold',
  color: '#fff',
};

// New styles for enhanced color controls
const colorControlsStyle: React.CSSProperties = {
  marginTop: '6px',
  padding: '6px',
  backgroundColor: 'rgba(20, 20, 20, 0.8)',
  borderRadius: '3px',
  border: '1px solid #333',
};

const colorModeHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '6px',
};

const colorModeButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '2px 6px',
  fontSize: '10px',
  borderRadius: '2px',
};

const activeColorModeStyle: React.CSSProperties = {
  background: 'rgba(0, 150, 255, 0.8)',
  border: '1px solid #0088ff',
  color: '#fff',
};

const resetButtonStyle: React.CSSProperties = {
  background: 'rgba(200, 100, 100, 0.8)',
  border: '1px solid #aa4444',
  color: '#fff',
  cursor: 'pointer',
  padding: '2px 4px',
  fontSize: '10px',
  borderRadius: '2px',
};

const elementColorsStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const elementColorListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
};

const elementColorItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const elementLabelStyle: React.CSSProperties = {
  fontSize: '10px',
  color: '#ccc',
  minWidth: '12px',
};

const elementColorInputStyle: React.CSSProperties = {
  width: '20px',
  height: '16px',
  border: '1px solid #666',
  borderRadius: '2px',
  cursor: 'pointer',
};

export default SpeciesPanel;
